'use client';

import React, { useState } from 'react';
import EmergencyHeader from './EmergencyHeader';
import QuickActionCard from './QuickActionCard';
import EmergencyProtocolCard from './EmergencyProtocolCard';
import VetLocatorMap from './VetLocatorMap';
import SymptomChecker from './SymptomChecker';
import EmergencyKitChecklist from './EmergencyKitChecklist';
import PoisonControlDatabase from './PoisonControlDatabase';
import DisasterPreparedness from './DisasterPreparedness';

interface EmergencyResourcesInteractiveProps {
  quickActions: any[];
  protocols: any[];
  emergencyVets: any[];
  symptoms: any[];
  checklistItems: any[];
  toxicSubstances: any[];
  disasterTips: any[];
}

const EmergencyResourcesInteractive: React.FC<EmergencyResourcesInteractiveProps> = ({
  quickActions,
  protocols,
  emergencyVets,
  symptoms,
  checklistItems,
  toxicSubstances,
  disasterTips
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProtocols = protocols.filter(protocol =>
    protocol.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    protocol.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <EmergencyHeader onSearchChange={setSearchQuery} />

      <main className="max-w-7xl mx-auto px-4 pt-0 pb-12 space-y-12">
        <section>
          <h2 className="text-3xl font-headline font-bold text-foreground mb-6">Quick Emergency Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </div>
        </section>

        <section>
          <VetLocatorMap vets={emergencyVets} />
        </section>

        <section>
          <SymptomChecker symptoms={symptoms} />
        </section>

        <section>
          <h2 className="text-3xl font-headline font-bold text-foreground mb-6">Emergency Protocols</h2>
          <div className="grid lg:grid-cols-2 gap-6">
            {filteredProtocols.map((protocol, index) => (
              <EmergencyProtocolCard key={index} {...protocol} />
            ))}
          </div>
        </section>

        <section>
          <PoisonControlDatabase substances={toxicSubstances} />
        </section>

        <section>
          <EmergencyKitChecklist items={checklistItems} />
        </section>

        <section>
          <DisasterPreparedness tips={disasterTips} />
        </section>

        <section className="bg-gradient-to-r from-primary to-secondary text-white rounded-lg p-8 text-center">
          <h2 className="text-3xl font-headline font-bold mb-4">Need Immediate Help?</h2>
          <p className="text-lg mb-6 opacity-90">
            Our 24/7 emergency hotline connects you with veterinary professionals who can provide immediate guidance
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:1-800-PET-HELP"
              className="px-8 py-4 bg-white text-primary font-headline font-bold rounded-lg hover:shadow-xl transition-all duration-300"
            >
              Call 1-800-PET-HELP
            </a>
            <a
              href="tel:1-888-POISON-1"
              className="px-8 py-4 bg-warning text-foreground font-headline font-bold rounded-lg hover:shadow-xl transition-all duration-300"
            >
              Poison Control: 1-888-POISON-1
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default EmergencyResourcesInteractive;